/**
 * GraphModal — trend chart using recharts (extracted from standalone).
 *
 * Filters to numeric-result entries for a parameter, sorts by date,
 * renders a LineChart with optional ideal-range reference lines.
 */
import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  getIdealRangeForParameter,
} from '@reefapp/shared';
import type { TestLogEntry, ParameterKey } from '@reefapp/shared';

interface GraphDataPoint {
  date: string;
  value: number;
}

export interface GraphModalProps {
  logs: ReadonlyArray<TestLogEntry>;
  parameterKey: ParameterKey;
  onClose(): void;
}

export function GraphModal({ logs, parameterKey, onClose }: GraphModalProps) {
  const data: GraphDataPoint[] = useMemo(() => {
    return logs
      .filter(
        (log) =>
          log.parameterKey === parameterKey &&
          typeof log.result === 'number',
      )
      .map((log) => ({
        date: new Date(log.date).toLocaleDateString(),
        value: log.result as number,
      }))
      .reverse();
  }, [logs, parameterKey]);

  const idealRange = getIdealRangeForParameter(parameterKey);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-5 border-b border-slate-700">
          <h3 className="text-2xl font-bold text-slate-100">
            Trend for {parameterKey}
          </h3>
          <button
            className="p-1 text-slate-400 hover:text-slate-200 text-3xl font-semibold"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="p-6 flex-auto">
          {data.length > 1 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" />
                <YAxis domain={['auto', 'auto']} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    color: '#e2e8f0',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={parameterKey}
                  stroke="#06b6d4"
                  activeDot={{ r: 8 }}
                />
                {idealRange && (
                  <>
                    <ReferenceLine
                      y={idealRange.min}
                      stroke="#facc15"
                      strokeDasharray="5 5"
                      label={{
                        value: `Min ${idealRange.min}`,
                        fill: '#facc15',
                        fontSize: 11,
                      }}
                    />
                    <ReferenceLine
                      y={idealRange.max}
                      stroke="#facc15"
                      strokeDasharray="5 5"
                      label={{
                        value: `Max ${idealRange.max}`,
                        fill: '#facc15',
                        fontSize: 11,
                      }}
                    />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-400 py-20">
              Not enough data to draw a graph. Please log at least two
              numerical results for this parameter.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
